import { useEffect, useState } from "react";
import { useUpdateCalendarIntegration } from "services/calendar";
import { Box, IconButton, Typography, Collapse, Button } from "@mui/material";
import { CalendarIntegration } from "protogen/calendar_service_pb";
import Checkbox from "components/common/Checkbox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Trash2 } from "lucide-react";
import { calendarSyncStatus } from "./CalendarSyncStatus";
import { CLIENT_ID, REDIRECT_URI } from "./constants";
import { useTwoWaySyncEnabled } from "../useTwoWaySyncEnabled";
import useIsMobile from "components/hooks/useIsMobile";
import TruncatedText from "components/common/TruncatedText";

export default ({
  integration,
  onUpdate,
  defaultOpen = false,
}: {
  integration: CalendarIntegration;
  onUpdate: () => void;
  defaultOpen: boolean;
}) => {
  const { request } = useUpdateCalendarIntegration();
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  const isTwoWaySyncEnabled = useTwoWaySyncEnabled();

  useEffect(() => {
    if (defaultOpen) {
      setIsExpanded(true);
    }
  }, [defaultOpen]);
  const onDeleteIntegration = async () => {
    const response = await request({
      integrationRef: integration.ref,
      deactivate: true,
      activate: false,
      enableCalendarId: "",
      disableCalendarId: "",
    });
    if (response) {
      onUpdate();
    }
  };

  const onCalendarChange = async (calendarId: string, enabled: boolean) => {
    const response = await request({
      integrationRef: integration.ref,
      deactivate: false,
      activate: false,
      enableCalendarId: enabled ? calendarId : "",
      disableCalendarId: !enabled ? calendarId : "",
    });
    if (response) {
      onUpdate();
    }
  };

  const enabled = integration.status !== "deactivated";
  const [icon, byline] = calendarSyncStatus(
    integration.status as "active" | "deactivated",
    Number(integration.lastAccessedSec),
    integration.type,
  );

  const getAccessLevel = (scope: string) => {
    if (scope === "freebusy") {
      return { label: "Free/Busy" };
    } else if (scope === "read_write") {
      return { label: "Read and write" };
    } else {
      return { label: "Read only" };
    }
  };

  const accessLevel = getAccessLevel(integration.accessScope || "");

  const canUpgrade =
    isTwoWaySyncEnabled &&
    integration &&
    integration.type === "google" &&
    integration.accessScope !== "read_write";

  const handleUpgrade = () => {
    // Pass the old integration ref via OAuth state parameter
    // After OAuth succeeds, we'll deactivate this old integration
    const state = JSON.stringify({ replaceIntegrationRef: integration.ref });
    const path = "https://www.googleapis.com/auth/calendar";
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?scope=email%20${path}&access_type=offline&response_type=code&state=${encodeURIComponent(
      state,
    )}&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&prompt=consent`;
    window.location.href = authUrl;
  };

  const isMobile = useIsMobile();

  if (integration.status === "deactivated") return null;
  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        border: "1px solid #E2E3E4",
        borderRadius: "12px",
        background: "#FFF",
        width: "100%",
        padding: isMobile ? "16px" : "16px 24px",
      }}
    >
      <Box
        display="flex"
        flexDirection={"row"}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
          <Box
            display="flex"
            flexDirection={"column"}
            gap="4px"
            sx={{ cursor: !isExpanded ? "pointer" : undefined }}
            onClick={() => !isExpanded && setIsExpanded(true)}
          >
            <Box display="flex" alignItems="center" gap={"8px"}>
              {icon}
              <Typography
                variant="bodyHeavy"
                color="#262626"
              >{`${integration.name}`}</Typography>
            </Box>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{
                textOverflow: "ellipses",
                overflow: "hidden",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "start" : "center",
                gap: isMobile ? "2px" : "4px",
              }}
            >
              {byline}
              {isTwoWaySyncEnabled && accessLevel && (
                <Typography variant="bodySmall" color="text.secondary">
                  {!isMobile ? "•" : ""} {accessLevel.label || "Read only"}
                </Typography>
              )}
            </Typography>
            {canUpgrade && isMobile && (
              <Button
                variant="text"
                color="primary"
                onClick={handleUpgrade}
                sx={{
                  width: "fit-content",
                  height: "auto",
                }}
              >
                Enable edit access
              </Button>
            )}
          </Box>
        </Box>
        <Box display="flex" gap="8px" alignItems="center">
          {canUpgrade && !isMobile && (
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={handleUpgrade}
              sx={{ padding: "0px 8px !important" }}
            >
              Enable edit access
            </Button>
          )}
          <IconButton aria-label="delete" onClick={onDeleteIntegration}>
            <Trash2 width="20" color="#8E9598" />
          </IconButton>
          <IconButton
            sx={{
              visibility: integration.calendars?.length ? "visible" : "hidden",
            }}
            aria-label="expand"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        {integration.calendars?.length > 0 && (
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            alignItems={"start"}
            marginTop="12px"
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap="8px" width="100%">
              {integration.calendars.map((cal) => {
                return (
                  <Box
                    key={cal.id}
                    display="flex"
                    alignItems="center"
                    gap="8px"
                    width="100%"
                  >
                    <Checkbox
                      disabled={!enabled}
                      checked={cal.enabled}
                      onChange={(e) =>
                        onCalendarChange(cal.id, e.target.checked)
                      }
                    />
                    <Box
                      display="flex"
                      flexDirection={isMobile ? "column" : "row"}
                      width="100%"
                    >
                      <Typography
                        variant="bodyHeavy"
                        color="text.primary"
                        sx={{
                          textWrap: "nowrap",
                          textOverflow: "ellipses",
                          overflow: "hidden",
                          flex: 1,
                        }}
                      >
                        {cal.summary}
                      </Typography>
                      <TruncatedText
                        variant="body"
                        color="text.secondary"
                        sx={{
                          textWrap: "none",
                          textOverflow: "ellipses",
                          overflow: "hidden",
                          flex: 1,
                        }}
                      >
                        {cal.id}
                      </TruncatedText>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
